/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 03-Aug-2026
 * @author      : Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Automation Plan Engine A2 — build-spec §3.4, §7 (D45/D52) — Provider Web
 * Purpose      : Authoring types for the PROVIDER-scoped automation plan master catalog, plus the
 *                pure config/step -> wire mapping. The per-kind config shapes here mirror
 *                AutomationStepConfigValidator exactly, because that validator REJECTS unknown
 *                keys — an extra field is a 400, not an ignored value.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * A2 §3.4/§7     | 03-Aug-2026 | Aravindan      | Initial creation — provider master authoring
 * AB#29707       | 02-Sep-2026 | Shabnam Banu H | toStepDTO derives AFTER_ENROLMENT for a DELAYED
 *                                (Claude-         FIRST step instead of AFTER_PREVIOUS, which had
 *                                assisted)        no base instant on step 1 and made every tenant
 *                                                 fork of such a master fail with AU131
 *                                                 ("Could not use that template"). Adds
 *                                                 AFTER_ENROLMENT to ScheduleMode. Mirrors the
 *                                                 tenant-web fix of 2026-08-25 (3243d524d9).
 */

/**
 * ==================== WHY THESE SHAPES ARE NOT NEGOTIABLE ====================
 *
 * `PUT /api/v1/automation-plans/masters/{uid}/versions/{version}/steps` runs every step through
 * `AutomationStepConfigValidator.validate(kind, config)` BEFORE any write. That validator calls
 * `rejectUnknownKeys` per kind: a config carrying a key the kind does not define is a 400 with
 * error code `AU0xx`, not a silently dropped field. So the per-kind interfaces below are the
 * closed key sets, and `buildStepConfig` emits nothing outside them.
 *
 * A provider master fans out VERBATIM to every tenant fork, so this is the one place a wrong shape
 * multiplies. Everything here is kept deliberately literal against the validator rather than
 * generalised into something convenient.
 * ============================================================================
 */

// ── step kinds ───────────────────────────────────────────────────────────────

/**
 * The 11 authorable kinds. `meeting` is absent on purpose — `REJECTED_LEGACY_KINDS` in the
 * validator refuses it outright (dropped per D26), so offering it here would build an editor whose
 * every save 400s.
 */
export type ActionKind =
  | 'sms'
  | 'email'
  | 'task'
  | 'reminder'
  | 'stage'
  | 'branch'
  | 'pay'
  | 'review'
  | 'notify'
  | 'moveplan'
  | 'wait';

export const ACTION_TYPES: Array<{ k: ActionKind; label: string; desc: string }> = [
  { k: 'task', label: 'GoPAL Task', desc: 'Create a GoPAL task for the team' },
  { k: 'reminder', label: 'Contact Reminder', desc: 'Schedule a reminder to reach out' },
  { k: 'sms', label: 'SMS', desc: 'Text the customer' },
  { k: 'email', label: 'Email', desc: 'Send a branded email' },
  { k: 'pay', label: 'Send PO / Invoice / Payment Link', desc: 'Send a document or payment link' },
  { k: 'stage', label: 'Update Deal Stage / Move to Plan', desc: 'Advance the deal or switch plans' },
  { k: 'branch', label: 'If / Else Branch', desc: 'Split the plan on a condition' },
  { k: 'review', label: 'Ask for a Review', desc: 'Request a review + referral' },
  { k: 'notify', label: 'Send Push Notification', desc: 'Push a notification to a GMS user' },
  { k: 'moveplan', label: 'Move to Plan', desc: 'Move the enrolment onto another plan' },
  { k: 'wait', label: 'Wait for', desc: 'Hold the plan for a period, a date, or a response' },
];

/**
 * What the provider base-plan builder OFFERS in the Action-type dropdown — the SAME five kinds the
 * tenant builder ships (PR 7897), in the same order: SMS, Email, Move to Plan, Send Push
 * Notification, Wait for. ACTION_TYPES still carries the legacy kinds (task/reminder/pay/stage/
 * branch/review) so an existing step of one still renders in the picker, but the menu offers only
 * these five.
 */
export const BUILDER_ACTION_KINDS: readonly ActionKind[] = ['sms', 'email', 'moveplan', 'notify', 'wait'];

export const actionLabel = (kind: ActionKind): string =>
  ACTION_TYPES.find((a) => a.k === kind)?.label ?? kind;

// ── scheduling ───────────────────────────────────────────────────────────────

export type DelayUnit = 'Minutes' | 'Hours' | 'Days' | 'Weeks';
export const DELAY_UNITS: DelayUnit[] = ['Minutes', 'Hours', 'Days', 'Weeks'];

/**
 * `PlanStepUpsertDTO.scheduleMode`. The two anchor values are NOT authored here — see below.
 *
 * `AFTER_ENROLMENT` = "the delay is measured from the ENROLMENT instant". It is what a delayed
 * FIRST step must carry, and it is only ever legal on a first step (server RULE C / AU132).
 *
 * ADDED 2026-09-02. Enrolment itself is a TENANT-side runtime concept and nothing in this app
 * enrols anybody — but `AutomationPlanService.fork()` deep-copies a master's steps VERBATIM into
 * every tenant that forks it, so whatever mode this app writes is the mode the tenant plan runs
 * with. A delayed first step saved as AFTER_PREVIOUS has no base instant to measure from once it
 * reaches a tenant: `AutomationEnrolmentService.initialFireAt` refuses the enrolment with AU055,
 * and the fork is now refused outright with AU131 ("Could not use that template"). So this app has
 * to author the value even though it never evaluates it.
 */
export type ScheduleMode =
  | 'IMMEDIATE' | 'AFTER_ENROLMENT' | 'AFTER_PREVIOUS' | 'BEFORE_ANCHOR' | 'AFTER_ANCHOR';

/**
 * ANCHOR SCHEDULING AND RECURRENCE ARE OFF, AND NOT PARTIALLY BUILT.
 *
 * The tenant PlanBuilder gates both behind `ANCHOR_AUTHORING_FLAG` because a saved anchor-mode
 * step never sends (nothing resolves the anchor date) and a saved recurrence fires exactly once
 * (repeat advancement is A6). Neither is enabled there, and `openfeature` is not a dependency of
 * provider-web at all — so rather than add a flag provider with nothing behind it, these editors
 * are simply not built here and the wire fields are never populated.
 *
 * `PlanStepUpsertDTO` carries `anchorKind`, `anchorParam`, `recurrenceKind`, `recurIntervalNum`,
 * `recurIntervalUnit`, `recurOffsets`, `recurUntil`, `recurMaxCount`, `recurUntilDate`. This client
 * OMITS every one of them, so the server's own defaults apply (`recurrenceKind='NONE'`,
 * `recurUntil='DEACTIVATED'`, anchors null). That is the correct no-op — it is not the same thing
 * as sending nulls, and it is not a claim that the fields do not exist.
 *
 * WHEN A6 LANDS: add the editors, populate the fields in `toStepDTO`, and gate on a real flag.
 */
export const ANCHOR_AND_RECURRENCE_AUTHORING_ENABLED = false;

/** The immediate/delayed split the UI actually authors. Maps onto `scheduleMode` on the wire. */
export type DeliveryMode = 'immediate' | 'delayed';

// assignee (assignToKind/assignToRef, task/reminder role assignment) was REMOVED (2026-09-01,
// AU031): no executor was ever built for task/reminder (AutomationKindRouter refuses both,
// AU073) and this screen never offered them as selectable step types (BUILDER_ACTION_KINDS =
// sms/email/moveplan/notify/wait only) — the columns and this screen's role picker were dead
// weight that had already caused one production defect (a communication step round-tripping a
// stale role reference). See provider_release_1_5_3_2/schema and release_1_5_3_2/schema for the
// DROP COLUMN migrations.

// ── branch condition (D17 / §8.4) ────────────────────────────────────────────

export type BranchOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'greaterThan'
  | 'lessThan'
  | 'isEmpty'
  | 'isNotEmpty';

/** The closed set `AutomationStepConfigValidator.BRANCH_OPERATORS` checks against. */
export const BRANCH_OPERATORS: BranchOperator[] = [
  'equals', 'notEquals', 'contains', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty',
];

export const BRANCH_OPERATOR_LABEL: Record<BranchOperator, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  greaterThan: 'is greater than',
  lessThan: 'is less than',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
};

/** The two operators for which the server does NOT require `condition.value`. */
export const VALUELESS_OPERATORS: BranchOperator[] = ['isEmpty', 'isNotEmpty'];

export interface BranchFieldDef {
  field: string;
  label: string;
  operators: BranchOperator[];
  valueKind: 'stage' | 'recipientType' | 'number';
}

/**
 * THREE fields, matching the tenant catalog exactly — and for the same reason: §8.4 restricts the
 * catalog to values the runner already holds, and `AutomationRunnerService.processOne` loads only
 * the enrolment row plus the one sales-stage lookup. A provider master authored against a wider
 * catalog would produce branches no tenant fork could evaluate.
 */
export const BRANCH_FIELDS: BranchFieldDef[] = [
  {
    field: 'recipient.salesStage',
    label: 'Deal stage',
    operators: ['equals', 'notEquals', 'isEmpty', 'isNotEmpty'],
    valueKind: 'stage',
  },
  {
    field: 'enrolment.recipientType',
    label: 'Contact kind',
    operators: ['equals', 'notEquals'],
    valueKind: 'recipientType',
  },
  {
    field: 'enrolment.messageCount',
    label: 'Messages sent so far in this plan',
    operators: ['equals', 'notEquals', 'greaterThan', 'lessThan'],
    valueKind: 'number',
  },
];

export const branchFieldDef = (field: string | undefined): BranchFieldDef | undefined =>
  BRANCH_FIELDS.find((f) => f.field === field);

export const RECIPIENT_TYPES = ['LEAD', 'CUSTOMER', 'SUBCONTRACTOR', 'VENDOR', 'EMPLOYEE'];

// ── vocabularies ─────────────────────────────────────────────────────────────

export interface StageOption {
  key: string;
  label: string;
}

/**
 * The nine CRM pipeline stages, keys and labels copied from the tenant CRM's `STAGE_META`.
 *
 * DUPLICATED, and flagged as such. provider-api exposes no stage-vocabulary read (`StageOptionDTO`
 * exists in provider-data but no controller returns it), and provider-web cannot import from
 * gmscloud-tenant-web — two separate CRA apps, no shared component or type library. So the list is
 * restated here. It drifts silently if the CRM adds a stage.
 *
 * WHEN a `GET /api/v1/automation-plans/stages` (or equivalent) lands: fetch it, and delete this.
 * `entry_stage` is VARCHAR(32), so the KEY is what goes on the wire, never the label.
 */
export const DEFAULT_STAGES: StageOption[] = [
  { key: 'new', label: 'New lead' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'inspection', label: 'Inspection scheduled' },
  { key: 'inspected', label: 'Inspected' },
  { key: 'estimate', label: 'Estimate sent' },
  { key: 'negotiation', label: 'In negotiation' },
  { key: 'scheduled', label: 'Job scheduled' },
  { key: 'won', label: 'Job complete' },
  { key: 'lost', label: 'Turned down' },
];

export const CONTACT_TYPES = ['All', 'Residential', 'Commercial', 'Insurance'];

export const LEAD_SOURCES = [
  'Angies', 'Dispatch', 'Facebook', 'Hubspot', 'Website', 'Referral', 'Google', 'Thumbtack',
];

/** `AutomationStepConfigValidator.PAY_DOCS` — an exact enum, not a display list. */
export const PAY_DOCS = ['Invoice', 'PO', 'Payment Link'];

/** `AutomationStepConfigValidator.STAGE_ACTIONS`. */
export type StageAction = 'stage' | 'plan';
export const STAGE_ACTIONS: StageAction[] = ['stage', 'plan'];

/** `AutomationStepConfigValidator.WAIT_MODES`. */
export type WaitMode = 'days' | 'date' | 'response';
export const WAIT_MODES: WaitMode[] = ['days', 'date', 'response'];

/** `AutomationStepConfigValidator.WAIT_ACTIONS`. */
export type WaitAction = 'branch' | 'plan' | 'loop' | 'wait_more';
export const WAIT_ACTIONS: WaitAction[] = ['branch', 'plan', 'loop', 'wait_more'];

// ── per-kind bounds, all taken from the validator ────────────────────────────

export const MAX_ALT_NUMBERS = 3;             // §6.1 guardrail 2
export const ALT_NUMBER_MAX_LENGTH = 32;      // LEN_PHONE_32
export const MAX_CHECKLIST_ITEMS = 100;       // MAX_CHECKLIST_ITEMS
export const CHECKLIST_ITEM_MAX_LENGTH = 200; // LEN_CHECKLIST_ITEM_200
export const LEN_NAME_200 = 200;              // label / notifyTitle / condition.field & .value
export const LEN_SUBJECT_300 = 300;           // email.subject
export const LEN_STAGE_32 = 32;               // stage.targetStage / wait.waitWindowUnit
export const LEN_REF_128 = 128;               // review.reviewPlatform
export const LEN_BODY_2000 = 2000;            // sms.message / task.notes / review.reviewMsg
export const LEN_EMAIL_BODY = 20000;          // email.body
export const LEN_NOTIFY_BODY_240 = 240;       // notify.notifyBody

// ── the authoring step ───────────────────────────────────────────────────────

/**
 * One step as the editor holds it: every scalar is a STRING while being typed (so a part-typed
 * "1" and an empty field are both representable), and `buildStepConfig` is what coerces and prunes
 * on the way to the wire. The field NAMES per kind are the config keys from §3.4 verbatim, so
 * there is no rename layer to get wrong.
 */
export interface PlanMasterStep {
  /** Local React key AND the client-correlation `stepUid` sent for chain linking. */
  id: string;
  kind: ActionKind;
  label: string;
  stepOrder: number;
  addNotifications: boolean;

  deliveryMode: DeliveryMode;
  delayNum: string;
  delayUnit: DelayUnit;
  /** `HH:mm` from an `<input type="time">`; widened to `HH:mm:ss` for the wire's LocalTime. */
  delayTime?: string;

  // sms
  smsTemplate?: string;
  message?: string;
  /** Comma/semicolon separated while typing; split into the wire array on save. */
  altNumbers?: string;
  excludeClient?: boolean;

  // email
  subject?: string;
  template?: string;
  body?: string;

  // task
  notes?: string;
  checklistItems?: string[];
  autoStart?: boolean;
  autoComplete?: boolean;

  // stage
  stageAction?: StageAction;
  targetStage?: string;
  /** stage(stageAction='plan') and moveplan both use this key. */
  targetPlan?: string;

  // branch
  condition?: BranchCondition;
  ifPlan?: string;
  elsePlan?: string;

  // pay
  payDoc?: string;
  /** `public.invoice.id` — a positive integer, NOT a uuid. */
  invoiceId?: string;

  // review
  reviewPlatform?: string;
  reviewMsg?: string;

  // notify
  notifyTitle?: string;
  notifyBody?: string;

  // wait
  waitMode?: WaitMode;
  waitDays?: string;
  waitTrigger?: string;
  waitWindowNum?: string;
  waitWindowUnit?: string;
  metAction?: WaitAction | '';
  unmetAction?: WaitAction | '';
}

export interface BranchCondition {
  field?: string;
  operator?: BranchOperator | '';
  value?: string;
}

// ── the authoring plan ───────────────────────────────────────────────────────

export type PlanMasterStatus = 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED' | 'RETIRED';

/**
 * Plan-level authoring state.
 *
 * ------------------------- READ THIS BEFORE ADDING A FIELD -------------------------
 * Two fields reach the server today, and no more:
 *   * `name` — on `POST /masters` (`createDraft`) ONLY. It is write-once: there is no rename
 *     endpoint, which is why the builder renders the name read-only rather than as a field.
 *   * `description` — on the plan-level `PUT /masters/{uid}/versions/{version}`
 *     (`updatePlanAttributes`). That PUT exists now, but `AutomationPlanMasterApi.updateAttributes`
 *     reads `body.get("description")` and calls `updateDraftDescription`, so `description` is the
 *     only key it binds; anything else in the body is ignored silently.
 *
 * `enabled`, `entryStage`, `contactTypes`, `leadSource`, `specificDate`, `nextPlanMasterUid` and
 * `businessDaysOnly` are all real columns on `automation_plan_master` with no endpoint to write
 * them, and this task was scoped out of `src/api`.
 *
 * `color` used to be listed here too. It is GONE (2026-08-17) — column, DTO field and model field —
 * because no colour picker was ever built on this builder or the tenant one, so it never held an
 * authored value. The tenant UI derives a plan's dot colour from its uid instead
 * (`planColorFor`, gmscloud-tenant-web `components/leadsCustomers/utils/AutomationPlanHelpers`).
 * Do not add it back here.
 *
 * They are modelled and rendered anyway, marked "not saved yet" at the point of authoring —
 * the same treatment the tenant PlanBuilder gives its own unsaveable fields, and for the same
 * reason: the alternative is a control that reports success and loses the value on refresh.
 *
 * WHEN a plan-level upsert lands, `planLevelFieldsArePersisted` becomes true and the notices
 * key off it — there is one flag to flip, not nine call sites to find.
 * ----------------------------------------------------------------------------------
 */
export const planLevelFieldsArePersisted = true;

export interface PlanMaster {
  /** `automation_plan_master.plan_master_uid`. Absent until the draft has been created. */
  planMasterUid?: string;
  version: number;
  status: PlanMasterStatus;
  name: string;
  description: string;
  enabled: boolean;
  entryStage: string;
  contactTypes: string;
  leadSource: string;
  specificDate: string;
  /** The MASTER this plan hands off to — a `plan_master_uid`, never a tenant plan uid. */
  nextPlanMasterUid: string;
  businessDaysOnly: boolean;
  steps: PlanMasterStep[];
}

export const emptyPlanMaster = (): PlanMaster => ({
  version: 1,
  status: 'DRAFT',
  name: '',
  description: '',
  enabled: true,
  entryStage: DEFAULT_STAGES[0].key,
  contactTypes: CONTACT_TYPES[0],
  leadSource: '',
  specificDate: '',
  nextPlanMasterUid: '',
  businessDaysOnly: false,
  steps: [],
});

export const newStep = (stepOrder: number): PlanMasterStep => ({
  id: newCorrelationUid(),
  kind: 'email',
  label: '',
  stepOrder,
  addNotifications: false,
  deliveryMode: 'immediate',
  delayNum: '1',
  delayUnit: 'Days',
  delayTime: '',
  stageAction: 'stage',
  payDoc: PAY_DOCS[0],
  waitMode: 'days',
  waitDays: '1',
  excludeClient: false,
  autoStart: false,
  autoComplete: false,
  checklistItems: [],
  condition: {},
});

/**
 * `stepUid`/`nextStepUid` are CLIENT-CORRELATION uuids: `AutomationPlanMasterApi.toMasterSteps`
 * matches a step's `nextStepUid` against another step's `stepUid` WITHIN THE SAME REQUEST and then
 * discards both, issuing fresh server uids. So these must be well-formed uuids and must not be
 * expected to survive the round trip.
 *
 * `crypto.randomUUID` is not universally available (it needs a secure context); the fallback keeps
 * authoring working over plain http in dev rather than throwing at "add step".
 */
export const newCorrelationUid = (): string => {
  const cryptoRef = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// ── wire DTOs ────────────────────────────────────────────────────────────────

/**
 * Mirrors `PlanMasterSummaryDTO`. `contributedByTenant` is the tenant that SHARED this plan as a
 * draft (null = provider-authored). The provider-api already returns it (populated in toSummaryDTO);
 * it is carried here so the authoring list can gate actions by origin — a tenant-contributed plan is
 * Publish/Delete only (no Edit), per the contribution model.
 */
export interface PlanMasterSummaryDTO {
  planMasterUid: string;
  version: number;
  name: string | null;
  description: string | null;
  status: string;
  /** Optional: absent on older payloads/fixtures; a falsy value means provider-authored. */
  contributedByTenant?: string | null;
  /** Plan-level settings for the read-only preview (all optional/nullable). entryStage + goal* =
   *  the master "Trigger". */
  leadSource?: string | null;
  entryStage?: string | null;
  nextPlanMasterUid?: string | null;
  goalKind?: string | null;
  goalParam?: string | null;
}

/**
 * Mirrors `PlanStepUpsertDTO`, restricted to the fields this app actually authors.
 *
 * The anchor and recurrence fields are deliberately absent rather than declared-and-null: see
 * `ANCHOR_AND_RECURRENCE_AUTHORING_ENABLED`. `templateMasterUid` is likewise absent — the per-kind
 * `config` carries the template reference (`sms.smsTemplate`, `email.template`), which is where the
 * validator looks for it.
 */
export interface PlanStepUpsertDTO {
  stepUid: string;
  nextStepUid: string | null;
  stepOrder: number;
  kind: ActionKind;
  label: string | null;
  addNotifications: boolean;
  scheduleMode: ScheduleMode;
  delayNum: number | null;
  delayUnit: DelayUnit;
  /** `LocalTime` — `HH:mm:ss`, or null. */
  delayTime: string | null;
  config: Record<string, unknown>;
}

/** Body of `POST /api/v1/automation-plans/masters`. The controller reads a `Map<String,String>`. */
export interface CreateDraftRequest {
  name: string;
  /** Set only when promoting a tenant-contributed plan; omitted for provider-authored ones. */
  contributedByTenant?: string;
  /** Plan-level fields carried on create so the unified builder makes a whole plan in ONE Save
   *  (2026-08-13). All optional; omitted keys stay null server-side. */
  description?: string | null;
  leadSource?: string | null;
  entryStage?: string | null;
  nextPlanMasterUid?: string | null;
  goalKind?: string | null;
  goalParam?: string | null;
}

// ── mapping to the wire ──────────────────────────────────────────────────────

const trimmedOrUndefined = (raw: string | undefined): string | undefined => {
  const t = (raw ?? '').trim();
  return t === '' ? undefined : t;
};

const STRICT_INTEGER = /^-?\d+$/;

/** "1.5" and "" are NOT 1. Matches the tenant payload mapper's `wholeNumber`. */
export const wholeNumber = (raw: string | undefined): number | null => {
  const t = (raw ?? '').trim();
  if (t === '' || !STRICT_INTEGER.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) ? n : null;
};

/** Splits the altNumbers text field the same way the count shown to the author does. */
export const splitAltNumbers = (raw: string | undefined): string[] =>
  (raw ?? '').split(/[,;]/).map((n) => n.trim()).filter((n) => n !== '');

/** `HH:mm` -> `HH:mm:00`. A blank or already-widened value is passed through unchanged. */
export const toLocalTime = (raw: string | undefined): string | null => {
  const t = (raw ?? '').trim();
  if (t === '') return null;
  return /^\d{2}:\d{2}$/.test(t) ? `${t}:00` : t;
};

/**
 * Builds the `config` object for one step, emitting ONLY keys the step's kind defines and ONLY
 * those the author actually filled in.
 *
 * Blank-pruning is not tidiness. `optionalUuidString` rejects `""` as a malformed uuid, and
 * `optionalEnumString` rejects `""` as outside the enum — so sending an empty string for an
 * untouched optional field turns a valid save into a 400. Absent is the only correct
 * representation of "not set".
 */
export const buildStepConfig = (step: PlanMasterStep): Record<string, unknown> => {
  const config: Record<string, unknown> = {};
  const put = (key: string, value: unknown): void => {
    if (value !== undefined) config[key] = value;
  };

  switch (step.kind) {
    case 'sms': {
      put('smsTemplate', trimmedOrUndefined(step.smsTemplate));
      put('message', trimmedOrUndefined(step.message));
      const alt = splitAltNumbers(step.altNumbers);
      if (alt.length > 0) put('altNumbers', alt);
      if (step.excludeClient) put('excludeClient', true);
      return config;
    }
    case 'email': {
      put('subject', trimmedOrUndefined(step.subject));
      put('template', trimmedOrUndefined(step.template));
      put('body', trimmedOrUndefined(step.body));
      return config;
    }
    case 'task': {
      put('notes', trimmedOrUndefined(step.notes));
      const items = (step.checklistItems ?? []).map((i) => i.trim()).filter((i) => i !== '');
      if (items.length > 0) put('checklistItems', items);
      if (step.autoStart) put('autoStart', true);
      if (step.autoComplete) put('autoComplete', true);
      return config;
    }
    case 'reminder': {
      put('notes', trimmedOrUndefined(step.notes));
      return config;
    }
    case 'stage': {
      const action = step.stageAction ?? 'stage';
      put('stageAction', action);
      // Only the companion the chosen action requires is sent. Sending the other one too would
      // save a target the runner never reads, and read back as a configured value.
      if (action === 'stage') put('targetStage', trimmedOrUndefined(step.targetStage));
      else put('targetPlan', trimmedOrUndefined(step.targetPlan));
      return config;
    }
    case 'branch': {
      const condition: Record<string, unknown> = {};
      const field = trimmedOrUndefined(step.condition?.field);
      const operator = step.condition?.operator;
      if (field) condition.field = field;
      if (operator) condition.operator = operator;
      const needsValue = !!operator && !VALUELESS_OPERATORS.includes(operator as BranchOperator);
      if (needsValue) {
        const def = branchFieldDef(field);
        const raw = (step.condition?.value ?? '').trim();
        if (raw !== '') {
          // A numeric field's value must be a JSON number: `enrolment.messageCount` is compared
          // numerically, and "3" as a string is a different comparison from 3.
          condition.value = def?.valueKind === 'number' ? (wholeNumber(raw) ?? raw) : raw;
        }
      }
      config.condition = condition;
      put('ifPlan', trimmedOrUndefined(step.ifPlan));
      put('elsePlan', trimmedOrUndefined(step.elsePlan));
      return config;
    }
    case 'pay': {
      put('payDoc', trimmedOrUndefined(step.payDoc));
      // `amount` is REJECTED outright by the validator (§8.5 — resolved against the invoice at
      // send time), so it is not modelled and must never be added here.
      const invoiceId = wholeNumber(step.invoiceId);
      if (invoiceId !== null) config.invoiceId = invoiceId;
      return config;
    }
    case 'review': {
      put('reviewPlatform', trimmedOrUndefined(step.reviewPlatform));
      put('reviewMsg', trimmedOrUndefined(step.reviewMsg));
      return config;
    }
    case 'notify': {
      put('notifyTitle', trimmedOrUndefined(step.notifyTitle));
      put('notifyBody', trimmedOrUndefined(step.notifyBody));
      return config;
    }
    case 'moveplan': {
      put('targetPlan', trimmedOrUndefined(step.targetPlan));
      return config;
    }
    case 'wait': {
      const mode = step.waitMode ?? 'days';
      config.waitMode = mode;
      if (mode === 'days') {
        const days = wholeNumber(step.waitDays);
        if (days !== null) config.waitDays = days;
      } else if (mode === 'response') {
        put('waitTrigger', trimmedOrUndefined(step.waitTrigger));
        const windowNum = wholeNumber(step.waitWindowNum);
        if (windowNum !== null) config.waitWindowNum = windowNum;
        put('waitWindowUnit', trimmedOrUndefined(step.waitWindowUnit));
        if (step.metAction) config.metAction = step.metAction;
        if (step.unmetAction) config.unmetAction = step.unmetAction;
      }
      // mode === 'date' carries no static field — it resolves per enrolment.
      return config;
    }
    default:
      return config;
  }
};

/**
 * Maps one authoring step to its wire DTO. `nextStepUid` chains to the NEXT step in list order,
 * which is what makes step order and the chain agree; `AutomationPlanMasterApi` resolves each link
 * against the correlation uids in this same request and 400s (AU026) on any that does not match.
 *
 * `scheduleMode` is DERIVED FROM POSITION, never stored on the authoring step — the same rule
 * tenant-web's `buildStep` follows. That is deliberate: a step dragged from position 3 to position
 * 1 must change base instant with nobody editing it, and a stored mode would go stale against the
 * step's actual position on the first reorder.
 */
export const toStepDTO = (
  step: PlanMasterStep,
  index: number,
  all: PlanMasterStep[],
): PlanStepUpsertDTO => {
  const delayed = step.deliveryMode === 'delayed';
  const timed = delayed && (step.delayUnit === 'Days' || step.delayUnit === 'Weeks');
  const next = all[index + 1];
  return {
    stepUid: step.id,
    nextStepUid: next ? next.id : null,
    stepOrder: index + 1,
    kind: step.kind,
    label: trimmedOrUndefined(step.label) ?? null,
    addNotifications: !!step.addNotifications,
    // A delayed FIRST step is AFTER_ENROLMENT, not AFTER_PREVIOUS: its delay is measured from the
    // enrolment instant, because there is no previous step to follow. This line was
    // `delayed ? 'AFTER_PREVIOUS' : 'IMMEDIATE'` until 2026-09-02, which published masters whose
    // step 1 could never be scheduled — the tenant's fork of such a master is refused with AU131
    // ("Could not use that template … Nothing was created"), so the defect surfaced on whichever
    // tenant clicked the template first, far from the provider who authored it. Tenant-web fixed
    // the same line on 2026-08-25 (AutomationPlanPayload.ts, commit 3243d524d9); this is the
    // missing half of that fix. `index` is 0-based and `stepOrder` is `index + 1` above, so
    // index === 0 IS the first step.
    scheduleMode: delayed ? (index === 0 ? 'AFTER_ENROLMENT' : 'AFTER_PREVIOUS') : 'IMMEDIATE',
    delayNum: delayed ? wholeNumber(step.delayNum) : null,
    delayUnit: step.delayUnit,
    delayTime: timed ? toLocalTime(step.delayTime) : null,
    config: buildStepConfig(step),
  };
};

export const toStepDTOs = (steps: PlanMasterStep[]): PlanStepUpsertDTO[] =>
  steps.map((step, index, all) => toStepDTO(step, index, all));

// ── mapping back from the wire ───────────────────────────────────────────────

/**
 * What `GET /api/v1/automation-plans/masters/{uid}/versions/{version}/steps` returns per element.
 *
 * Every field is declared nullable and every scalar widened to its raw JSON type, deliberately:
 * this is a READ of rows that were not necessarily written by THIS editor. A step saved before the
 * ROLE-only assignee rule, or one carrying an anchor `scheduleMode` this app does not author, is a
 * row that exists — the mapper's job is to represent it honestly, not to assume it came from here.
 * `PlanStepUpsertDTO` is the narrow WRITE shape and stays narrow; this is its wide read counterpart.
 */
export interface PlanStepReadDTO {
  stepUid: string | null;
  nextStepUid: string | null;
  stepOrder: number | null;
  kind: string;
  label: string | null;
  addNotifications: boolean | null;
  scheduleMode: string | null;
  delayNum: number | null;
  delayUnit: string | null;
  /** `LocalTime` — `HH:mm:ss`, narrowed back to the `HH:mm` an `<input type="time">` accepts. */
  delayTime: string | null;
  config: Record<string, unknown> | null;
}

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Text as the editor holds it — `undefined` for anything not actually present. */
const readText = (raw: unknown): string | undefined => {
  if (typeof raw === 'string') return raw.trim() === '' ? undefined : raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return undefined;
};

/** Every numeric config value comes back as a JSON number and is edited as a string. */
const readNumberText = (raw: unknown): string | undefined => {
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  return undefined;
};

const readStrings = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((i): i is string => typeof i === 'string') : [];

/**
 * `config` is a jsonb column. A server that hands it back as a STRING rather than an object is not
 * hypothetical (jsonb round-trips through a String field in more than one mapper), and the
 * difference is invisible until every per-kind field reads as empty — which, on a whole-set-replace
 * editor, is a wipe. Parsing defensively costs one try/catch.
 */
const readConfig = (raw: unknown): Record<string, unknown> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return {};
    }
  }
  return {};
};

/** `HH:mm:ss` -> `HH:mm`. `<input type="time">` shows nothing for a value carrying seconds. */
export const fromLocalTime = (raw: string | null | undefined): string | undefined => {
  const t = (raw ?? '').trim();
  if (t === '') return undefined;
  const m = /^(\d{2}:\d{2})(:\d{2})?/.exec(t);
  return m ? m[1] : t;
};

const isDelayUnit = (raw: unknown): raw is DelayUnit =>
  typeof raw === 'string' && (DELAY_UNITS as string[]).includes(raw);

/**
 * The inverse of `buildStepConfig`, key for key.
 *
 * It reads only the keys its kind defines — the same closed sets `buildStepConfig` emits — so a
 * value the validator would reject cannot be picked up here and sent back on the next save. A key
 * present in `config` that the kind does not define is ignored, which is the read-side counterpart
 * of `rejectUnknownKeys`.
 */
const applyConfig = (kind: ActionKind, config: Record<string, unknown>): Partial<PlanMasterStep> => {
  switch (kind) {
    case 'sms':
      return {
        smsTemplate: readText(config.smsTemplate),
        message: readText(config.message),
        // Re-joined with the separator `splitAltNumbers` splits on, so the count the author sees
        // after a load equals the count that was saved.
        altNumbers: readStrings(config.altNumbers).join(', ') || undefined,
        excludeClient: config.excludeClient === true,
      };
    case 'email':
      return {
        subject: readText(config.subject),
        template: readText(config.template),
        body: readText(config.body),
      };
    case 'task':
      return {
        notes: readText(config.notes),
        checklistItems: readStrings(config.checklistItems),
        autoStart: config.autoStart === true,
        autoComplete: config.autoComplete === true,
      };
    case 'reminder':
      return { notes: readText(config.notes) };
    case 'stage': {
      const action = readText(config.stageAction);
      return {
        stageAction: (STAGE_ACTIONS as string[]).includes(action ?? '')
          ? (action as StageAction)
          : 'stage',
        targetStage: readText(config.targetStage),
        targetPlan: readText(config.targetPlan),
      };
    }
    case 'branch': {
      const raw = readConfig(config.condition);
      const operator = readText(raw.operator);
      return {
        condition: {
          field: readText(raw.field),
          // `value` was written as a JSON number for the numeric field; back to text for the input.
          operator: (BRANCH_OPERATORS as string[]).includes(operator ?? '')
            ? (operator as BranchOperator)
            : '',
          value: readNumberText(raw.value),
        },
        ifPlan: readText(config.ifPlan),
        elsePlan: readText(config.elsePlan),
      };
    }
    case 'pay':
      return {
        payDoc: readText(config.payDoc) ?? PAY_DOCS[0],
        invoiceId: readNumberText(config.invoiceId),
      };
    case 'review':
      return {
        reviewPlatform: readText(config.reviewPlatform),
        reviewMsg: readText(config.reviewMsg),
      };
    case 'notify':
      return {
        notifyTitle: readText(config.notifyTitle),
        notifyBody: readText(config.notifyBody),
      };
    case 'moveplan':
      return { targetPlan: readText(config.targetPlan) };
    case 'wait': {
      const mode = readText(config.waitMode);
      const met = readText(config.metAction);
      const unmet = readText(config.unmetAction);
      return {
        waitMode: (WAIT_MODES as string[]).includes(mode ?? '') ? (mode as WaitMode) : 'days',
        waitDays: readNumberText(config.waitDays),
        waitTrigger: readText(config.waitTrigger),
        waitWindowNum: readNumberText(config.waitWindowNum),
        waitWindowUnit: readText(config.waitWindowUnit),
        metAction: (WAIT_ACTIONS as string[]).includes(met ?? '') ? (met as WaitAction) : '',
        unmetAction: (WAIT_ACTIONS as string[]).includes(unmet ?? '') ? (unmet as WaitAction) : '',
      };
    }
    default:
      return {};
  }
};

/**
 * One persisted step back into the authoring model.
 *
 * `stepUid` is REUSED as the local id when it is a well-formed uuid. That is safe and not a
 * contradiction of the correlation-uid rule: the server discards the submitted uids and issues
 * fresh ones on every replace, so reusing the read value only keeps React keys and the
 * `nextStepUid` chain internally consistent within the next request. A non-uuid (or absent) value
 * gets a fresh correlation uid rather than being sent back and rejected as malformed.
 */
export const fromStepDTO = (dto: PlanStepReadDTO, index: number): PlanMasterStep => {
  const kind = (readText(dto.kind) ?? 'email') as ActionKind;
  const uid = readText(dto.stepUid);
  return {
    ...newStep(index + 1),
    id: uid && UUID_PATTERN.test(uid) ? uid : newCorrelationUid(),
    kind,
    label: dto.label ?? '',
    stepOrder: index + 1,
    addNotifications: dto.addNotifications === true,
    // IMMEDIATE is the only mode this editor authors as "immediate"; AFTER_ENROLMENT,
    // AFTER_PREVIOUS and both anchor modes all present as "delayed". See `anchorScheduledSteps`
    // for why the anchor pair is flagged and this pair is not.
    //
    // Collapsing AFTER_ENROLMENT to "delayed" loses nothing, because `toStepDTO` re-derives it
    // from `index === 0` on the way back out. That round trip is what makes re-saving a legacy
    // master REPAIR it: a step 1 loaded as AFTER_PREVIOUS reads back as "delayed" and is written
    // out as AFTER_ENROLMENT, with delayNum/delayUnit untouched — the same conversion the
    // tenant-side migration V1_5_2_0_40 performed on tenant rows, which its provider-side sibling
    // V1_5_2_0_22 deliberately did not perform on master rows ("rewriting a provider's authored
    // template is the provider's call, not a migration's").
    deliveryMode: dto.scheduleMode === 'IMMEDIATE' ? 'immediate' : 'delayed',
    delayNum: dto.delayNum != null ? String(dto.delayNum) : '1',
    delayUnit: isDelayUnit(dto.delayUnit) ? dto.delayUnit : 'Days',
    delayTime: fromLocalTime(dto.delayTime),
    ...applyConfig(kind, readConfig(dto.config)),
  };
};

/**
 * Orders by `stepOrder` and renumbers 1..n.
 *
 * `stepOrder`, not the `nextStepUid` chain: `toStepDTO` derives the chain FROM list order on every
 * save, so order is the authoritative half of the pair and following the chain instead would make
 * the two disagree on the first reordering.
 */
export const fromStepDTOs = (dtos: PlanStepReadDTO[]): PlanMasterStep[] =>
  [...dtos]
    .sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0))
    .map((dto, index) => fromStepDTO(dto, index));

/**
 * ==================== WHAT A ROUND TRIP THROUGH THIS EDITOR CHANGES ====================
 *
 * `PUT …/steps` is a whole-set replace, so re-saving a loaded set rewrites every step — including
 * the parts of a step this app cannot author. Three of those are detectable on read, and each is
 * reported rather than silently normalised, because the author is the only one who can decide
 * whether the rewrite is acceptable.
 * ======================================================================================
 */

/** Steps whose `scheduleMode` is an ANCHOR mode — re-saving downgrades them to AFTER_PREVIOUS. */
export const anchorScheduledSteps = (dtos: PlanStepReadDTO[]): number[] =>
  dtos
    .map((d, i) => (d.scheduleMode === 'BEFORE_ANCHOR' || d.scheduleMode === 'AFTER_ANCHOR' ? i + 1 : 0))
    .filter((n) => n > 0);

/** Steps of a kind this editor has no form for — its config cannot be shown or preserved. */
export const unknownKindSteps = (dtos: PlanStepReadDTO[]): number[] =>
  dtos
    .map((d, i) => (ACTION_TYPES.some((a) => a.k === d.kind) ? 0 : i + 1))
    .filter((n) => n > 0);

// ── display helpers ──────────────────────────────────────────────────────────

export const statusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'PUBLISHED':
      return 'Published';
    case 'SUPERSEDED':
      return 'Superseded';
    case 'RETIRED':
      return 'Retired';
    default:
      return String(status ?? 'Unknown');
  }
};

export const statusColor = (
  status: string | null | undefined,
): 'default' | 'info' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'PUBLISHED':
      return 'success';
    case 'DRAFT':
      return 'info';
    case 'RETIRED':
      return 'error';
    case 'SUPERSEDED':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * SUPERSEDED is not a synonym for RETIRED, and conflating them is a live hazard: RETIRED carries a
 * RUNTIME meaning (`end_reason = PLAN_RETIRED`), so a surface treating "my fork's master is
 * RETIRED" as a stop signal would end every enrolment on every fork of the previous version. The
 * entity javadoc spells this out. Only DRAFT is publishable.
 *
 * There is deliberately no `isRetirable` here. It existed, unreferenced, after Retire was dropped
 * from the provider catalog UI (see `AutomationPlansPage`'s `PendingAction`); a status predicate
 * with no caller reads as a live rule the screens are enforcing. `retireVersion` is still exported
 * by `AutomationMasterService` — restoring the button means restoring this one-liner with it.
 */
export const isPublishable = (status: string): boolean => status === 'DRAFT';

/** A new version can only be opened from a PUBLISHED one — `newVersion` 404s otherwise. */
export const canOpenNewVersion = (status: string): boolean => status === 'PUBLISHED';

export const timingLabel = (step: PlanMasterStep): string => {
  if (step.deliveryMode === 'immediate') return 'Immediate';
  const time = step.delayTime ? ` · ${step.delayTime}` : '';
  return `+${step.delayNum || '0'} ${step.delayUnit}${time}`;
};
